import { useContext, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router";
import { StaffWorkspaceContext } from "../../../app/session";
import { api } from "../../../data";
import { ErrorState, Loading } from "../../../components/States";
import { Dialog } from "../../../components/ui";
import { Icon } from "../../../components/Icon";
import { WEBSITE_URL } from "./parts";
import { Vergleich, baldText, basisLeistungen, modulAufKarte, inVorbereitung, tarifFuerWen, tarifMarke, useModulBereit } from "./TarifVergleich";
import { date, money } from "../../../lib/format";
import { t } from "../../../lib/sprache";
import { MODUL_KEYS, enterprisePreis, isCreatorPlan, planName, preisNettoHinweis } from "../../../model/tarif";
import { useTesttage } from "../../../components/Tarif";
import { empfehlungAusTest, useTestumfang } from "./Lebenszyklus";
import { fehlerText } from "../../../lib/fehler";
import { useBfcacheRueckkehr } from "../../../lib/bfcache";
import { billingChange, loadAbo, recordB2bDeclaration } from "./data";
import { PaypalAbschluss, paypalVerfuegbar } from "./PaypalAbschluss";
import { B2bBestaetigung } from "./Lebenszyklus";
import { TestkontoLoeschen } from "./TestkontoLoeschen";
import { b2bMissingText, creatorNurUnternehmerText, nettoPreis, testphaseKostenText, type LifecycleStatus } from "./model";

/**
 * 0169: Neue Agenturen hinterlegen vor der Nutzung eine Zahlungsmethode (Entscheidung des Inhabers 02.10.2026). Statt der
 * Inhalte zeigt das Portal den Abschluss im Tarif der Wahl: mit den Tagen der Testphase (Stripe bucht am Ende automatisch ab,
 * vorher kostenlos kündbar) oder sofort („Sofort starten“). Nur der Admin schließt ab; Manager sehen einen Hinweis.
 * Die Wahl „Sofort starten“ aus der Preisseite (?kaufen=1) gilt seit 27.10.2026 nicht mehr (Z-1): erster Durchgang immer mit Test.
 * Anwaltsvorgaben 06.10.2026: Preis mit „netto“ direkt am Betrag (Creator-Tarif mit „ausschließlich für Unternehmer …“),
 * Kosten der Testphase mit konkretem Datum (Ende der Testphase der Organisation, Zeitzone des Kontos) und eine eigene,
 * nicht vorangekreuzte B2B-Bestätigung vor jedem Abschluss (gespeichert mit Weg „checkout“, 0182). Mechanik unverändert.
 */
const KAUF_KEY = "tk-einrichtung-kaufen";
export function rememberKauf(on: boolean) {
  try {
    if (on) localStorage.setItem(KAUF_KEY, "1");
    else localStorage.removeItem(KAUF_KEY);
  } catch {
    /* ohne Speicher: Testphase als Voreinstellung */
  }
}
/** Z-1 (Inhaber 27.10.2026): Im ersten Durchgang gibt es keinen Sofortkauf mehr; die gemerkte Wahl von der Preisseite gilt nicht */
export function recallKauf(): boolean {
  try {
    localStorage.removeItem(KAUF_KEY);
    return false;
  } catch {
    return false;
  }
}

export function ZahlungsmethodeStart({ orgId, isChef, life, timeZone }: { orgId: string; isChef: boolean; life: LifecycleStatus; timeZone: string }) {
  const abo = useQuery({ queryKey: ["abo", orgId], queryFn: () => loadAbo(api, orgId, timeZone), enabled: isChef });
  // Bauliste Vertrieb (Punkt 14): Wer bei Stripe abbricht, findet seine Auswahl wieder (nur in diesem Tab gemerkt)
  const gemerkt = gemerkteWahl(orgId);
  // K-374: ?wechsel=<tarif> (Knopf „Upgrade auf …“) wählt den Tarif vor
  const [plan, setPlan] = useState<string | null>(gemerkt?.plan ?? wechselAusLink());
  const [intervall, setIntervall] = useState<"month" | "year">(gemerkt?.intervall ?? "year"); // Jahrestarif zuerst (zwei Monate geschenkt)
  const abgebrochen = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("checkout") === "abgebrochen";
  const [vergleich, setVergleich] = useState(false);
  const [groesse, setGroesse] = useState<string | null>(null);
  // Punkt 15: Rabattcode nur auf Wunsch (ein leeres Feld schickt Leute auf Gutscheinsuche); aus dem Link (?code=) vorbelegt
  const [code, setCode] = useState(() => gemerkterCode());
  const [codeOffen, setCodeOffen] = useState(() => !!gemerkterCode());
  const [busy, setBusy] = useState<"trial" | "kauf" | null>(null);
  const [paypal, setPaypal] = useState<"trial" | "kauf" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [b2b, setB2b] = useState(false);
  // B-1 (Inhaber 27.10.2026): USt-IdNr. freiwillig vor dem Stripe-Schritt (der Modus „nur Zahlungsart speichern“ fragt sie nicht ab)
  const [ust, setUst] = useState("");
  const [b2bFehler, setB2bFehler] = useState<string | null>(null);
  // 0205 (Inhaber 13.10.2026): Zahlungsmethode für die laufende Testphase überspringen; danach fragt das Portal erst nach Ablauf wieder
  const qc = useQueryClient();
  const navigate = useNavigate();
  const creatorKonto = !!useContext(StaffWorkspaceContext)?.isCreator;
  // K-375: noch nicht freigeschaltete Module (Instagram-Insights u. a.) als „in Vorbereitung“, nicht als Leistung
  const bereit = useModulBereit();
  // K-497: Testdauer aus platform_defaults; L (K-491): Empfehlung aus dem Test als „Empfohlen für dich“
  const testtage = useTesttage();
  const empfohlen = empfehlungAusTest(useTestumfang(orgId, isChef && life.status === "trial" && !creatorKonto).data);
  const [skipBusy, setSkipBusy] = useState(false);
  const wahlRef = useRef<string | null>(null);
  // K-097 wie useAction: „busy“ sperrt die Knöpfe erst nach dem nächsten Zeichnen; die Sperre hier greift sofort, damit ein
  // Doppelklick weder zwei B2B-Bestätigungen noch zwei Checkout-Sitzungen erzeugt (erster echter Kunde 24.10.2026)
  const laeuftRef = useRef(false);
  // Zurück aus Stripe (Seite aus dem bfcache): Sperre und „Zahlung wird geöffnet …“ zurücksetzen, sonst hinge der Knopf
  useBfcacheRueckkehr(() => { laeuftRef.current = false; setBusy(null); });
  const ueberspringen = async () => {
    const gewaehlt = wahlRef.current;
    setSkipBusy(true);
    setError(null);
    try {
      // Der gewählte Tarif gilt auch im Test ohne Zahlungsdaten (er zeigt, wohin es nach dem Test geht)
      if (gewaehlt && gewaehlt !== life.plan_key) await api.rpc("org_trial_choose_plan", { p_org: orgId, p_plan: gewaehlt }).catch(() => undefined);
      await api.rpc("trial_skip_payment", { p_org: orgId });
      await qc.invalidateQueries({ queryKey: ["org_lifecycle", orgId] });
      // Danach in den Schnellstart (Aktivierungsplan Vertrieb 16.10.2026), nicht auf die Seite, auf der man zufällig stand (etwa
      // Abo & Tarif nach einem abgebrochenen Stripe-Abschluss; Inhaber 13.10.2026). Creator-Konten: Übersicht mit Startleitfaden.
      navigate(creatorKonto ? "/agentur" : "/agentur/schnellstart", { replace: true });
    } catch (e) {
      setError(fehlerText(e));
    } finally {
      setSkipBusy(false);
    }
  };
  const kauf = recallKauf();
  const ende = life.trial_ends_at ? date(life.trial_ends_at, timeZone) : null;
  // 0372: wer im Test die Zahlungsart hinterlegt, bekommt einmal Tage dazu; Stripe bzw. PayPal bucht entsprechend später ab
  const bonusTage = Math.max(0, Number(life.payment_bonus_days ?? 0) || 0);
  const endeMitBonus = life.trial_ends_at ? date(new Date(Date.parse(life.trial_ends_at) + bonusTage * 86_400_000).toISOString(), timeZone) : null;
  // Stripe verlangt mindestens 48 Stunden bis zum Ende der Testphase (wie keepTrial in billing-self-service.ts)
  // K-545: die 48 Stunden, die Stripe bis zum Testende verlangt, zählen ab dem verlängerten Ende (Bonustage, wie billing-change)
  const testphaseMoeglich = !!life.trial_ends_at && Date.parse(life.trial_ends_at) + Math.max(0, Number(life.payment_bonus_days ?? 0) || 0) * 86_400_000 > Date.now() + 49 * 3600 * 1000;

  if (!isChef) {
    return (
      <div className="panel stack tk-empty" role="status">
        <h1 className="tk-state-title">{t("Gleich geht es los", "Almost ready")}</h1>
        <p>{t("Deine Agentur startet, sobald der Admin eine Zahlungsmethode hinterlegt oder ein Abo abschließt. Wende dich bitte an ihn.", "Your agency starts as soon as the admin adds a payment method or takes out a subscription. Please contact them.")}</p>
      </div>
    );
  }
  if (abo.isLoading) return <Loading />;
  if (abo.error || !abo.data) return <ErrorState error={abo.error} />;
  const plans = abo.data.plans.filter((p) => p.active && (intervall === "year" ? p.stripe_price_year : p.stripe_price_month));
  const gewaehlt = (plan && plans.some((p) => p.key === plan) ? plan : null) ?? (plans.some((p) => p.key === life.plan_key) ? life.plan_key! : plans[0]?.key ?? null);
  wahlRef.current = gewaehlt;
  const preis = (cents: number) => nettoPreis(money(cents, "EUR"), intervall);
  const gewaehlterPlan = plans.find((p) => p.key === gewaehlt) ?? null;
  // Inhaber 21.10.2026: überall nur der Monatsbetrag, bei jährlicher Abrechnung mit dem Zusatz „jährlich abgerechnet“
  const gewaehlterPreis = gewaehlterPlan ? money(Math.round(intervall === "year" ? gewaehlterPlan.price_year_cents / 12 : gewaehlterPlan.price_month_cents), "EUR") : null;
  const danachText = gewaehlterPlan && gewaehlterPreis
    ? t(`Danach ${planName(gewaehlterPlan)} ${gewaehlterPreis} netto im Monat${intervall === "year" ? ", jährlich abgerechnet" : ""}.`,
      `Then ${planName(gewaehlterPlan)} ${gewaehlterPreis} net per month${intervall === "year" ? ", billed annually" : ""}.`)
    : null;
  // Z-4: Testende näher als ein Tag (Konto ohne Zahlungsart)
  const endetHeute = !!life.trial_ends_at && Date.parse(life.trial_ends_at) - Date.now() < 86_400_000;
  // B2B-Bestätigung vor jedem Abschluss (Stripe oder PayPal); gespeichert, bevor es zum Zahlungsdienst geht
  const bestaetigt = async (): Promise<boolean> => {
    if (!b2b) { setB2bFehler(b2bMissingText()); return false; }
    setB2bFehler(null);
    await recordB2bDeclaration(api, "checkout", orgId);
    return true;
  };
  // PayPal: erst bestätigen, dann das Formular für die Rechnungsangaben öffnen; auch hier nur einmal je Klick
  const zuPaypal = async (art: "trial" | "kauf") => {
    if (laeuftRef.current) return;
    laeuftRef.current = true;
    setError(null);
    try {
      if (await bestaetigt()) setPaypal(art);
    } catch (e) {
      setError(fehlerText(e));
    } finally {
      laeuftRef.current = false;
    }
  };

  const abschliessen = async (art: "trial" | "kauf") => {
    if (!gewaehlt || laeuftRef.current) return;
    laeuftRef.current = true;
    setBusy(art);
    setError(null);
    try {
      if (!(await bestaetigt())) { setBusy(null); laeuftRef.current = false; return; }
      const r = await billingChange(api, orgId, { action: "checkout", plan: gewaehlt, interval: intervall, ...(art === "kauf" ? { ohne_testphase: true } : {}), ...(code.trim() ? { code: code.trim() } : {}), ...(ust.trim() ? { vat_id: ust.trim() } : {}) });
      if (!r.url) throw new Error(t("Die Zahlungsseite ließ sich nicht öffnen. Bitte gleich noch einmal versuchen.", "The payment page could not be opened. Please try again shortly."));
      rememberKauf(false);
      merkeWahl(orgId, gewaehlt, intervall);
      // Die Sperre bleibt bis zum Wechsel auf die Stripe-Seite stehen (sonst zweite Sitzung während des Ladens)
      window.location.assign(r.url);
    } catch (e) {
      setError(fehlerText(e));
      setBusy(null);
      laeuftRef.current = false;
    }
  };

  // Paketkarten (Inhaber 13.10.2026: „man muss verstehen, welches Paket zu wem passt“): Grenzen und Module aus dem Katalog,
  // je Karte nur das, was gegenüber dem kleineren Tarif dazukommt
  const wert = (planKey: string, key: string): number | null => abo.data!.entitlements.find((e) => e.plan_key === planKey && e.key === key)?.value ?? null;
  const moduleVon = (planKey: string): string[] => MODUL_KEYS.filter((k) => (wert(planKey, k) ?? 0) >= 1);
  const zahl = (n: number | null, einheit: string, unbegrenzt: string): string | null => (n === null ? null : n < 0 ? unbegrenzt : `${n} ${einheit}`);
  const karten = plans.map((p, i) => {
    const vorher = i > 0 ? plans[i - 1]! : null;
    const dazu = moduleVon(p.key).filter((k) => !vorher || !moduleVon(vorher.key).includes(k))
      .sort((a, b) => Number(inVorbereitung(a, bereit)) - Number(inVorbereitung(b, bereit)));
    const cents = intervall === "year" ? p.price_year_cents : p.price_month_cents;
    return {
      plan: p, cents, vorher, dazu,
      proMonat: intervall === "year" ? money(Math.round(p.price_year_cents / 12), "EUR") : null,
      // Punkt 5: Ersparnis der jährlichen Abrechnung in Euro, aus den Preisen des Katalogs gerechnet
      ersparnis: Math.max(0, p.price_month_cents * 12 - p.price_year_cents),
      // Punkt 7: Vergleich mit Einzelprogrammen wie auf der Preisseite (dort mit Rechenweg)
      // Inhaber 21.10.2026: Vergleichswert unabhängig von der Abrechnung (Monatswert der Einzeltools)
      einzeltools: EINZELTOOLS[p.key]?.month ?? null,
      grenzen: [
        zahl(wert(p.key, "max_seats"), wert(p.key, "max_seats") === 1 ? t("Platz für dich", "seat for you") : t("Plätze fürs Team", "team seats"), t("Team ohne Grenze", "unlimited team seats")),
        zahl(wert(p.key, "max_talents"), t("Creator", "creators"), t("Creator ohne Grenze", "unlimited creators")),
        zahl(wert(p.key, "max_contracts_per_month"), t("Verträge im Monat", "contracts per month"), t("Verträge ohne Grenze", "unlimited contracts")),
        zahl(wert(p.key, "storage_gb"), t("GB Speicher", "GB storage"), t("Speicher ohne Grenze", "unlimited storage")),
      ].filter((x): x is string => !!x),
    };
  });
  // Punkt 13 (Entscheidung Yannik 13.10.2026): Hauptknopf ist der Test ohne Zahlungsdaten; wer von „Sofort starten“ der
  // Preisseite kommt, behält den Sofortkauf als Hauptknopf
  const haupt: "ohne" | "trial" | "kauf" = kauf ? "kauf" : life.can_skip_payment ? "ohne" : testphaseMoeglich ? "trial" : "kauf";
  // Inhaber 21.10.2026: neuer Zahlungsschritt (Zahlungsart Pflicht, Test möglich, nicht von „Sofort starten“ gekommen)
  const gratisWeg = !life.can_skip_payment && testphaseMoeglich && !kauf;
  // Punkt 4: Frage nach der Größe entlang der echten Tarifgrenzen (nur Agentur-Konten mit den drei Tarifen)
  const grenzeSolo = wert("start", "max_talents");
  const grenzeTeam = wert("team", "max_talents");
  const mitFrage = !creatorKonto && ["start", "team", "agentur"].every((k) => plans.some((x) => x.key === k)) && !!grenzeSolo && !!grenzeTeam && grenzeSolo > 0 && grenzeTeam > grenzeSolo;
  const stufen = mitFrage ? [
    { key: "start", label: t(`bis ${grenzeSolo}`, `up to ${grenzeSolo}`) },
    { key: "team", label: t(`${grenzeSolo! + 1} bis ${grenzeTeam}`, `${grenzeSolo! + 1} to ${grenzeTeam}`) },
    { key: "agentur", label: t(`mehr als ${grenzeTeam}`, `more than ${grenzeTeam}`) },
  ] : [];

  return (
    <div className="tk-tarifwahl stack">
      <div className="tk-tarifwahl-kopf stack">
        <h1>{t("Wähle deinen Tarif", "Choose your plan")}</h1>
        <p>{endetHeute
          ? t("Dein Test endet heute, du kannst direkt starten.", "Your trial ends today, you can start right away.")
          : ende && testphaseMoeglich
          ? t(`Du testest kostenlos bis zum ${ende}. Heute zahlst du nichts, und du kannst jederzeit kündigen oder den Tarif wechseln.`, `You try it free until ${ende}. You pay nothing today, and you can cancel or change plans at any time.`)
          : t("Du kannst jederzeit kündigen oder den Tarif wechseln.", "You can cancel or change plans at any time.")}</p>
        <AdresseSpaeter />
        {mitFrage && (
          <div className="tk-tarif-frage" role="group" aria-label={t("Wie viele Creator betreust du?", "How many creators do you manage?")}>
            <strong>{t("Wie viele Creator betreust du?", "How many creators do you manage?")}</strong>
            <div className="row">
              {stufen.map((st) => (
                <button key={st.key} type="button" className={groesse === st.key ? "primary" : undefined} aria-pressed={groesse === st.key} onClick={() => {
                  setGroesse(st.key); setPlan(st.key);
                  window.setTimeout(() => document.querySelector(".tk-tarif.gewaehlt")?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 60);
                }}>{st.label}</button>
              ))}
            </div>
          </div>
        )}
        <div className="tk-umschalter" role="radiogroup" aria-label={t("Abrechnung", "Billing")}>
          <label className={intervall === "month" ? "aktiv" : undefined}><input type="radio" name="intervall" checked={intervall === "month"} onChange={() => setIntervall("month")} /><span>{t("Monatlich", "Monthly")}</span></label>
          <label className={intervall === "year" ? "aktiv" : undefined}><input type="radio" name="intervall" checked={intervall === "year"} onChange={() => setIntervall("year")} /><span>{t("Jährlich", "Annually")}<small>{t("2 Monate geschenkt", "2 months free")}</small></span></label>
        </div>
      </div>
      <div className={`tk-tarife${karten.length === 1 ? " einzeln" : !creatorKonto ? " mit-enterprise" : karten.length === 2 ? " zwei" : ""}`} role="radiogroup" aria-label={t("Tarif", "Plan")}>
        {karten.map(({ plan: p, cents, vorher, dazu, proMonat, grenzen, einzeltools }) => (
          <label key={p.key} className={`tk-tarif${gewaehlt === p.key ? " gewaehlt" : ""}${p.key === (empfohlen ?? "team") ? " empfohlen" : ""}`}>
            <input type="radio" name="tarif" value={p.key} checked={gewaehlt === p.key} onChange={() => setPlan(p.key)} />
            {/* Inhaber 21.10.2026: Etikett „14 Tage kostenlos“ oben auf jeder Karte */}
            {testphaseMoeglich && !kauf && <span className="tk-tarif-etikett">{t(`${testtage} Tage kostenlos`, `${testtage} days free`)}</span>}
            <span className="tk-tarif-kopf">
              <strong>{planName(p)}</strong>
              {tarifMarke(p.key, empfohlen) && <span className="pill">{tarifMarke(p.key, empfohlen)}</span>}
            </span>
            <span className="tk-tarif-fuer">{tarifFuerWen(p.key)}</span>
            {/* Leistung zuerst: was der Tarif kann; der Umfang (Plätze, Creator, Verträge, Speicher) folgt als eine Zeile */}
            {vorher && <span className="tk-tarif-dazu">{t(`Alles aus ${planName(vorher)}, dazu:`, `Everything in ${planName(vorher)}, plus:`)}</span>}
            <ul className="tk-tarif-liste">
              {!vorher && basisLeistungen(p.key).map((x) => <li key={x}>{x}</li>)}
              {dazu.filter((k) => !inVorbereitung(k, bereit)).map((k) => <li key={k}>{modulAufKarte(k, bereit)}</li>)}
            </ul>
            {baldText(dazu, bereit) && <span className="tk-tarif-bald">{baldText(dazu, bereit)}</span>}
            <span className="tk-tarif-umfang">{grenzen.join(" · ")}</span>
            {/* Preis bewusst unten und ruhig (Inhaber 13.10.2026: die Leistung steht vorn); „netto“ bleibt direkt am Betrag */}
            {/* Inhaber 13.10.2026: immer der Monatspreis, wie bei anderen Anbietern; bei jährlicher Abrechnung der günstigere Monatspreis
                neben dem durchgestrichenen. Der Jahresbetrag steht einmal unten beim Abschluss („Mit Testphase: …“). */}
            <span className="tk-tarif-preis">
              <span className="num">{proMonat ? <><s>{money(p.price_month_cents, "EUR")}</s> {nettoPreis(proMonat, "month")}</> : preis(cents)}</span>
              {/* Inhaber 14.10.2026: der Vergleich direkt unter dem Preis, beide als Monatsbetrag, keine Zahl dazwischen; keine Jahressumme
                  auf der Karte (den Jahresbetrag nennt der Abschluss „Mit Testphase: …“ und Stripe) */}
              {einzeltools && <small className="tk-tarif-vergleich">{t(`Mit Einzeltools zahlst du geschätzt ≈ ${einzeltools} € im Monat*`, `With separate tools you pay an estimated ≈ €${einzeltools} a month*`)}</small>}
              <small>{proMonat ? jahrText(p.price_month_cents, p.price_year_cents) : t("Monatlich abgerechnet, monatlich kündbar", "Billed monthly, cancel monthly")}</small>
              {isCreatorPlan(p.key) && <small>{creatorNurUnternehmerText()}</small>}
            </span>
            <span className="tk-tarif-wahl" aria-hidden="true">{gewaehlt === p.key ? t("Ausgewählt", "Selected") : t(`${planName(p)} wählen`, `Choose ${planName(p)}`)}</span>
          </label>
        ))}
        {/* Inhaber 14.10.2026: Enterprise als vollwertige Karte wie die anderen; nicht wählbar, kein Checkout, Weg zum Gespräch */}
        {!creatorKonto && karten.length > 1 && <EnterpriseKarte />}
        {/* Am Handy liegen die Karten untereinander: Die Leiste hält die Wahl und den Weg zum Abschluss im Blick */}
        {gewaehlterPlan && (
          <div className="tk-tarif-leiste">
            <span><strong>{planName(gewaehlterPlan)}</strong> {t("ausgewählt", "selected")}</span>
            <button type="button" className="primary" onClick={() => document.getElementById("tarif-abschluss")?.scrollIntoView({ behavior: "smooth", block: "start" })}>{t("Weiter", "Continue")}</button>
          </div>
        )}
      </div>
      {!creatorKonto && karten.length > 1 && (
        <div className="tk-tarif-unter stack">
          <div className="row">
            {/* Punkt 11: Vergleich als Fenster über der Tarifwahl, die Auswahl bleibt erhalten */}
            <button type="button" className="quiet" onClick={() => setVergleich(true)}>{t("Alle Funktionen vergleichen", "Compare all features")}</button>
          </div>
          {karten.some((k) => k.einzeltools) && <small>{t("* Beispielrechnung für dieselbe Arbeit mit einzelnen Programmen. ", "* Example calculation for the same work with separate tools. ")}<a href={`${WEBSITE_URL}/preise.html#so-rechnen`} target="_blank" rel="noreferrer">{t("So rechnen wir", "How we calculate")}</a></small>}
          <Dialog open={vergleich} onClose={() => setVergleich(false)} title={t("Tarife im Vergleich", "Plans compared")} wide>
            {vergleich && <Vergleich data={abo.data} />}
          </Dialog>
        </div>
      )}
      <div id="tarif-abschluss" className="panel stack tk-tarif-abschluss">
        {abgebrochen && <p className="notice" role="status">{life.can_skip_payment ? t("Noch nichts gebucht. Du kannst auch erst ohne Zahlungsdaten testen.", "Nothing has been booked yet. You can also try without payment details first.") : t("Noch nichts gebucht.", "Nothing has been booked yet.")}</p>}
        {/* Punkt 13: drei Wege, je mit einer Zeile; der Hauptknopf ist der Test ohne Zahlungsdaten */}
        {life.can_skip_payment && (
          <div className="tk-tarif-weg">
            <button type="button" className={haupt === "ohne" ? "primary" : undefined} disabled={!!busy || skipBusy} aria-busy={skipBusy} onClick={() => void ueberspringen()}>
              {skipBusy ? t("Einen Moment …", "One moment …") : t(`${testtage} Tage kostenlos testen`, `Try ${testtage} days free`)}
            </button>
            <small>{ende ? t(`Ohne Zahlungsdaten, es wird nichts abgebucht. Du testest bis zum ${ende} im vollen Umfang.`, `No payment details, nothing is charged. You try the full feature set until ${ende}.`) : t("Ohne Zahlungsdaten, es wird nichts abgebucht.", "No payment details, nothing is charged.")}</small>
          </div>
        )}
        {/* Inhaber 21.10.2026: Zahlungsart wieder Pflicht für den Test (Neuanmeldungen). Klar sagen: komplett kostenlos, heute 0,00 €,
            Zeitstrahl bis zur ersten Abbuchung, Hauptknopf „Kostenlos testen starten“ (Stripe), PayPal gleichwertig daneben,
            „Sofort starten“ nur noch als Textlink */}
        {gratisWeg && (
          <div className="tk-tarif-gratis stack">
            <h2>{t(`${testtage} Tage komplett kostenlos testen`, `Try ${testtage} days completely free`)}</h2>
            <p className="tk-tarif-heute"><span>{t("Heute", "Today")}</span> <strong className="num">{t("0,00 €", "€0.00")}</strong></p>
            {endeMitBonus && (
              <ol className="tk-zeitstrahl" aria-label={t("Ablauf bis zur ersten Abbuchung", "Timeline until the first charge")}>
                {/* R-1/R-2 (Test-Chat 21.10.2026): zwei Punkte ohne doppelten Betrag, „bis dahin kündbar“ als Satz darunter */}
                <li><strong>{t("Heute: Test startet", "Today: trial starts")}</strong></li>
                <li><strong>{t(`${endeMitBonus}: erste Abbuchung`, `${endeMitBonus}: first charge`)}</strong></li>
              </ol>
            )}
            {endeMitBonus && <p className="tk-zeitstrahl-satz">{t("Bis dahin jederzeit mit einem Klick kündbar.", "Cancel any time before with one click.")}</p>}
            {danachText && <small className="tk-tarif-danach">{danachText}</small>}
            <UstFeld wert={ust} onChange={setUst} />
            <B2bBestaetigung id="checkout-b2b" checked={b2b} onChange={(v) => { setB2b(v); if (v) setB2bFehler(null); }} error={b2bFehler} />
            {error && <p className="tk-error" role="alert">{error}</p>}
            <div className="tk-tarif-weg">
              <button className="primary" type="button" disabled={!gewaehlt || !!busy} aria-busy={busy === "trial"} onClick={() => void abschliessen("trial")}>
                {busy === "trial" ? t("Zahlung wird geöffnet …", "Opening payment …") : t("Kostenlos testen starten", "Start free trial")}
              </button>
              {/* Inhaber 27.10.2026: Stripe zeigt im Modus „nur Zahlungsart speichern“ keinen Testhinweis oben, dafür den Pflichtsatz
                  zu künftigen Zahlungen; deshalb hier vorab sagen, was im nächsten Schritt passiert */}
              <small className="tk-tarif-naechster">{t("Im nächsten Schritt speicherst du nur deine Zahlungsart, abgebucht wird heute nichts.", "In the next step you only save your payment method, nothing is charged today.")}</small>
              {paypalVerfuegbar() && (
                <button type="button" className="tk-paypal-knopf" disabled={!gewaehlt || !!busy} onClick={() => void zuPaypal("trial")}>
                  <PaypalZeichen />{t("Mit PayPal kostenlos testen", "Try free with PayPal")}
                </button>
              )}
              {paypalVerfuegbar() && <small className="tk-paypal-zeile">{t("PayPal (auch per Lastschrift über dein PayPal-Bankkonto)", "PayPal (incl. bank debit via PayPal)")}</small>}
            </div>
            {paypal && <PaypalAbschluss orgId={orgId} plan={gewaehlt} interval={intervall} ohneTestphase={paypal === "kauf"} onAbbrechen={() => setPaypal(null)} />}
            {/* Inhaber 27.10.2026: im ersten Durchgang kein „Lieber ohne Test sofort starten“ mehr (führte auf die Stripe-Seite mit dem
                vollen Jahresbetrag); sofort kaufen geht später unter Abo & Tarif */}
            <small className="tk-tarif-sicher"><Icon name="lock" /><span>{paypalVerfuegbar() ? t("Sichere Zahlung über Stripe oder PayPal", "Secure payment via Stripe or PayPal") : t("Sichere Zahlung über Stripe", "Secure payment via Stripe")}{" · "}{zahlartenText()}</span></small>
          </div>
        )}
        {!gratisWeg && <div className="tk-tarif-mit stack">
          {life.can_skip_payment && <strong>{bonusTage > 0 && testphaseMoeglich
            ? t(`Oder direkt mit Zahlungsart, dann ${bonusTage} Tage Test extra:`, `Or with a payment method right away, with ${bonusTage} extra trial days:`)
            : t("Oder direkt mit Zahlungsart:", "Or with a payment method right away:")}</strong>}
          {endeMitBonus && testphaseMoeglich && gewaehlterPreis && life.can_skip_payment && (
            <p role="status"><strong>{t("Mit Testphase: ", "With trial: ")}</strong>{testphaseKostenText(endeMitBonus, gewaehlterPreis, "month")}{intervall === "year" ? t(" Jährlich abgerechnet.", " Billed annually.") : ""}</p>
          )}
          <UstFeld wert={ust} onChange={setUst} />
          <B2bBestaetigung id="checkout-b2b" checked={b2b} onChange={(v) => { setB2b(v); if (v) setB2bFehler(null); }} error={b2bFehler} />
          {error && <p className="tk-error" role="alert">{error}</p>}
          {testphaseMoeglich && (
            <div className="tk-tarif-weg">
              <button className={haupt === "trial" ? "primary" : undefined} type="button" disabled={!gewaehlt || !!busy} aria-busy={busy === "trial"} onClick={() => void abschliessen("trial")}>
                {busy === "trial" ? t("Zahlung wird geöffnet …", "Opening payment …") : life.can_skip_payment ? t("Zahlungsart gleich hinterlegen", "Add a payment method now") : t("Zahlungsart hinterlegen und kostenlos testen", "Add payment method and try for free")}
              </button>
              <small>{life.can_skip_payment
                ? t(`Dann läuft dein Konto nach dem Test ohne Unterbrechung weiter. Heute 0 €, erste Abbuchung am ${endeMitBonus ?? ende}.`, `Then your account continues after the trial without interruption. Today €0, first charge on ${endeMitBonus ?? ende}.`)
                : t("Im nächsten Schritt speicherst du nur deine Zahlungsart, abgebucht wird heute nichts. Nach dem Test läuft dein Konto ohne Unterbrechung im gewählten Tarif weiter.", "In the next step you only save your payment method, nothing is charged today. After the trial your account continues on the chosen plan without interruption.")}</small>
            </div>
          )}
          <div className="tk-tarif-weg">
            <button className={haupt === "kauf" ? "primary" : undefined} type="button" disabled={!gewaehlt || !!busy} aria-busy={busy === "kauf"} onClick={() => void abschliessen("kauf")}>
              {busy === "kauf" ? t("Zahlung wird geöffnet …", "Opening payment …") : t("Sofort starten", "Start now")}
            </button>
            <small>{t("Ohne Testphase, die erste Abbuchung ist heute.", "No trial, the first charge is today.")}</small>
          </div>
        </div>}
        {codeOffen
          ? <label className="tk-tarif-code">{t("Rabattcode", "Discount code")}<input type="text" value={code} maxLength={40} autoCapitalize="characters" autoComplete="off" onChange={(e) => setCode(e.target.value.replace(/[^A-Za-z0-9_-]/g, ""))} /><small>{t("Wird beim Abschluss mit Zahlungsart automatisch abgezogen.", "Applied automatically when you check out with a payment method.")}</small></label>
          : <div className="row"><button type="button" className="quiet" onClick={() => setCodeOffen(true)}>{t("Ich habe einen Code", "I have a code")}</button></div>}
        {/* Punkt 12: drei Vertrauenszeilen und drei Fragen; nur Aussagen, die stimmen */}
        <ul className="tk-tarif-vertrauen">
          {!gratisWeg && <li>{paypalVerfuegbar() ? t("Sichere Zahlung über Stripe oder PayPal", "Secure payment via Stripe or PayPal") : t("Sichere Zahlung über Stripe", "Secure payment via Stripe")}</li>}
          <li>{t("Rechnung zu jeder Zahlung", "An invoice for every payment")}</li>
          <li>{t("Kündigung direkt im Portal", "Cancel right in the portal")}</li>
        </ul>
        <div className="tk-tarif-fragen">
          <details><summary>{t("Wann wird zum ersten Mal abgebucht?", "When is the first charge?")}</summary>
            <p>{ende && testphaseMoeglich && life.can_skip_payment
              ? t(`Im Test ohne Zahlungsdaten gar nicht. Hinterlegst du eine Zahlungsart, am ${endeMitBonus ?? ende}.`, `Not at all in the trial without payment details. If you add a payment method, on ${endeMitBonus ?? ende}.`)
              : endeMitBonus && testphaseMoeglich
                ? t(`Erst am ${endeMitBonus}, nach dem kostenlosen Test.`, `Not until ${endeMitBonus}, after the free trial.`)
                : t("Bei „Sofort starten“ heute; danach zu Beginn jedes Abrechnungszeitraums.", "With “Start now”, today; after that at the start of each billing period.")}</p></details>
          <details><summary>{t("Welche Zahlungsarten gibt es?", "Which payment methods are there?")}</summary>
            <p>{zahlartenText()}.</p></details>
          <details><summary>{t("Kann ich den Tarif später wechseln?", "Can I change the plan later?")}</summary>
            <p>{t("Ja, jederzeit unter Einstellungen · Abo & Tarif. Dort kündigst du auch.", "Yes, at any time under Settings · Subscription & plan. That is also where you cancel.")}</p></details>
        </div>
        {paypalVerfuegbar() && !paypal && !gratisWeg && (
          <div className="row">
            <button type="button" className="quiet" disabled={!gewaehlt || !!busy} onClick={() => void zuPaypal(testphaseMoeglich && !kauf ? "trial" : "kauf")}>{t("Lieber mit PayPal bezahlen", "Prefer to pay with PayPal")}</button>
          </div>
        )}
        {paypal && !gratisWeg && <PaypalAbschluss orgId={orgId} plan={gewaehlt} interval={intervall} ohneTestphase={paypal === "kauf"} onAbbrechen={() => setPaypal(null)} />}
      </div>
      {life.can_skip_payment && <small className="tk-tarif-neben">{t("Ohne Zahlungsdaten verschickt das Portal höchstens 20 Mails am Tag an Creator und Marken.", "Without payment details the portal sends at most 20 emails per day to creators and brands.")}</small>}
      {life.can_delete_trial && <div className="row tk-tarif-neben"><TestkontoLoeschen orgId={orgId} /></div>}
      <small>{t("Kündigen kannst du jederzeit unter Einstellungen · Abo & Tarif; in der Testphase fällt dann nichts an.", "You can cancel at any time under Settings · Subscription & plan; during the trial nothing is charged.")}</small>
      {/* K-494: derselbe Satz an allen Preisen der App */}
      <small>{preisNettoHinweis()} {t("Eine USt-IdNr. ist nicht erforderlich, um CreatorOffice zu nutzen.", "A VAT ID is not required to use CreatorOffice.")}</small>
      <SupportZeile />
    </div>
  );
}


/** Hilfe bleibt im Zahlungsschritt erreichbar (layouts.tsx nimmt /agentur/hilfe von der Sperre aus). */
export function SupportZeile() {
  return (
    <small>{t("Fragen oder klappt etwas nicht?", "Questions, or something not working?")} <Link to="/agentur/hilfe">{t("Hilfe & Support", "Help & support")}</Link>{" "}
      {t("oder", "or")} <a href="mailto:support@creatoroffice.de">support@creatoroffice.de</a></small>
  );
}

/** Preise der Einzelprogramme wie auf der Preisseite (preise.html, dort mit Rechenweg); je Monat bei monatlicher bzw. jährlicher Zahlung.
 *  apps/web/test hält die Zahlen mit der Website gleich. */
/** Zeile unter dem Preis bei jährlicher Abrechnung: ohne Betrag, mit den geschenkten Monaten (Jahrespreis = 10 Monatspreise → 2) */
export function jahrText(monthCents: number, yearCents: number | null | undefined): string {
  const geschenkt = yearCents && monthCents > 0 ? Math.round(12 - yearCents / monthCents) : 0;
  if (geschenkt >= 1) {
    return t(`Jährlich abgerechnet · ${geschenkt === 1 ? "1 Monat" : `${geschenkt} Monate`} geschenkt`, `Billed yearly · ${geschenkt === 1 ? "1 month" : `${geschenkt} months`} free`);
  }
  return t("Jährlich abgerechnet", "Billed yearly");
}

/** Enterprise als vierte Karte (Inhaber 14.10.2026): gleich gestaltet, nicht wählbar, führt zum Gespräch */
function EnterpriseKarte() {
  return (
    <div className="tk-tarif tk-tarif-enterprise-karte" role="group" aria-label="Enterprise">
      <span className="tk-tarif-kopf"><strong>Enterprise</strong></span>
      <span className="tk-tarif-fuer">{t("Für große Managements und Verbünde", "For large managements and groups")}</span>
      <span className="tk-tarif-dazu">{t("Alles aus Agentur, dazu:", "Everything in Agency, plus:")}</span>
      <ul className="tk-tarif-liste">
        {[
          t("Creator und Plätze nach Bedarf", "Creators and seats as needed"),
          t("Mehrere Firmen und Standorte", "Several companies and locations"),
          t("Persönliche Einrichtung", "Personal onboarding"),
          t("Vorrang-Support", "Priority support"),
          t("Übernahme aus dem Altsystem", "Migration from your previous system"),
        ].map((x) => <li key={x}>{x}</li>)}
      </ul>
      <span className="tk-tarif-preis">
        <span className="num">{t(`ab ${enterprisePreis()} netto/Monat`, `from ${enterprisePreis()} net/month`)}</span>
        <small>{t("Individuelles Angebot nach einem Gespräch", "Individual offer after a call")}</small>
      </span>
      <a className="btn tk-tarif-wahl" href={`${WEBSITE_URL}/kontakt?thema=enterprise`} target="_blank" rel="noreferrer">{t("Gespräch vereinbaren", "Arrange a call")}</a>
    </div>
  );
}

export const EINZELTOOLS: Record<string, { month: number; year: number }> = {
  start: { month: 96, year: 72 }, team: { month: 372, year: 288 }, agentur: { month: 924, year: 696 },
};

const CODE_KEY = "tk-rabattcode";
/** Code aus dem Link der Registrierung (?code=…): bis zum Abschluss gemerkt */
export function rememberCode(code: string | null) {
  try {
    const c = (code ?? "").replace(/[^A-Za-z0-9_-]/g, "").slice(0, 40);
    if (c.length >= 3) localStorage.setItem(CODE_KEY, c);
  } catch { /* ohne Speicher: Code von Hand eintragen */ }
}
export function gemerkterCode(): string {
  try { return localStorage.getItem(CODE_KEY) ?? ""; } catch { return ""; }
}

/** Vorgewählter Tarif aus ?wechsel= (K-374); nur ein einfacher Schlüssel, ob es ihn gibt, prüft die Kartenliste */
function wechselAusLink(): string | null {
  if (typeof window === "undefined") return null;
  const v = (new URLSearchParams(window.location.search).get("wechsel") ?? "").trim().toLowerCase();
  return /^[a-z0-9_]{1,40}$/.test(v) ? v : null;
}

const WAHL_KEY = "tk-tarifwahl";
function merkeWahl(orgId: string, plan: string, intervall: "month" | "year") {
  try { sessionStorage.setItem(`${WAHL_KEY}:${orgId}`, JSON.stringify({ plan, intervall })); } catch { /* ohne Speicher: Vorauswahl wie beim ersten Besuch */ }
}
function gemerkteWahl(orgId: string): { plan: string; intervall: "month" | "year" } | null {
  try {
    const v = JSON.parse(sessionStorage.getItem(`${WAHL_KEY}:${orgId}`) ?? "null") as { plan?: unknown; intervall?: unknown } | null;
    return v && typeof v.plan === "string" && (v.intervall === "month" || v.intervall === "year") ? { plan: v.plan, intervall: v.intervall } : null;
  } catch {
    return null;
  }
}

/** Zahlungsarten, die wir wirklich anbieten (Inhaber 21.10.2026: Stripe-Konto in den VAE mit Karte und Link, kein SEPA; PayPal über
 *  den eigenen Weg). Apple Pay und Google Pay zeigt Stripe Checkout zur Karte, je nach Gerät. */
export function zahlartenText(): string {
  return paypalVerfuegbar() ? t("Karte, Apple Pay, Google Pay oder PayPal", "Card, Apple Pay, Google Pay or PayPal") : t("Karte, Apple Pay oder Google Pay", "Card, Apple Pay or Google Pay");
}

/** USt-IdNr. auf der Zahlungsseite: kein Pflichtfeld, ohne Zusatz „freiwillig“ (Inhaber 27.10.2026); eingetragen für Reverse Charge */
function UstFeld({ wert, onChange }: { wert: string; onChange: (v: string) => void }) {
  // Z-3: Platzhalter im Format des Sitzlands; Reverse Charge nur für Firmen außerhalb Deutschlands (deutsche Firmen zahlen Umsatzsteuer)
  const land = (useContext(StaffWorkspaceContext)?.org?.country ?? "DE").toUpperCase();
  const beispiel = land === "AT" ? "ATU12345678" : land === "CH" ? "CHE-123.456.789" : land === "DE" ? "DE123456789" : `${land}…`;
  return (
    <label className="stack tk-tarif-ust">
      <span>{t("USt-IdNr.", "VAT ID")}</span>
      <input type="text" value={wert} onChange={(e) => onChange(e.target.value)} autoComplete="off" spellCheck={false} maxLength={20}
        placeholder={t(`z. B. ${beispiel}`, `e.g. ${beispiel}`)} aria-describedby="tarif-ust-hinweis" />
      <small id="tarif-ust-hinweis">{land === "DE"
        ? t("Steht auf deiner Rechnung. Du kannst sie auch später in den Firmendaten eintragen.", "Shown on your invoice. You can also add it later in your company details.")
        : t("Für Firmen außerhalb Deutschlands: Mit USt-IdNr. stellen wir ohne Umsatzsteuer aus (Reverse Charge). Du kannst sie auch später in den Firmendaten eintragen.", "For companies outside Germany: with a VAT ID we invoice without VAT (reverse charge). You can also add it later in your company details.")}</small>
    </label>
  );
}

/** Kleines PayPal-Zeichen für den Knopf „Mit PayPal kostenlos testen“ (zwei versetzte P in den PayPal-Farben) */
function PaypalZeichen() {
  return (
    <svg className="tk-paypal-zeichen" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <path fill="#003087" d="M7.3 21H3.6L6.6 3h7.1c3.6 0 5.6 1.9 5 5.1-.6 3.5-3.2 5.3-6.7 5.3H9.4L8.2 21z" />
      <path fill="#009CDE" d="M10.3 22H7.5l2.4-14h5.4c2.8 0 4.3 1.5 3.8 4-.5 2.8-2.6 4.2-5.4 4.2h-2.1z" opacity=".9" />
    </svg>
  );
}

/**
 * „Später bestätigen“ (Inhaber 21.10.2026): Die Bestätigung der E-Mail-Adresse blockiert nichts (0247, Anmeldung ohne Code). Solange
 * sie offen ist, ein Satz im Zahlungsschritt bzw. in der Einrichtung; den Link aus der Mail kann man jederzeit nachholen (Hinweis im
 * Portal mit „Mail erneut senden“). Demo-Vorschau mit ?vorschau=adresse.
 */
export function AdresseSpaeter() {
  const vorschau = api.mode === "demo" && typeof window !== "undefined" && new URLSearchParams(window.location.search).get("vorschau") === "adresse";
  const q = useQuery({ queryKey: ["my_email_verification"], enabled: api.mode !== "demo", staleTime: 60_000,
    queryFn: () => api.read<{ verified: boolean; required: boolean; email: string } | null>("my_email_verification").catch(() => null) });
  const s = vorschau ? { verified: false, required: true, email: "lena.sommer@example.com" } : q.data;
  if (!s || !s.required || s.verified) return null;
  return <small className="tk-adresse-spaeter">{t(`Deine E-Mail-Adresse (${s.email}) bestätigst du später mit dem Link aus unserer Mail. Du kannst schon weitermachen.`,
    `You confirm your email address (${s.email}) later with the link in our email. You can carry on already.`)}</small>;
}
